/**
 * @file Validation, Formatter and Helper functions for the Web Balance Inquiry Form
 * @author Malik Shalash <mshalash@storedvalue.com>
 */

// maxLength attribute value for cardNoField input element
export const getMaxCardLength = (cardLength, formatCardNum = { enabled: false, digitsBetweenSpaces: 0 }) => {
  // If card number is to be formatted with spaces,
  // spaces need to be accounted for in the "max length" count
  if (formatCardNum.enabled) {
    const numOfSpaces = cardLength / formatCardNum.digitsBetweenSpaces;

    return parseInt(numOfSpaces, 10) + parseInt(cardLength, 10);
  }

  if (typeof cardLength === 'string') return cardLength;
  return Math.max.apply(null, cardLength).toString();
};

// maxLength attribute value for pinNoField input element
export const getMaxPinLength = pinLength => {
  if (typeof pinLength === 'string') return pinLength;
  return Math.max.apply(null, pinLength).toString();
};

export const hasInvalidInputs = (cardNo, pin, cardLength, pinLength, showPin, pinType, formatCardNum = { enabled: false, digitsBetweenSpaces: 0 }) => {
  let cardIsInvalid;
  let isCardNumbers;
  let currentCardLength;
  if (formatCardNum.enabled === true) {
    currentCardLength = cardNo.replace(/ /g, '').length; // Count length, excluding spaces
    isCardNumbers = /^[\d\s]+$/.test(cardNo); // Account for presence of spaces in regex test
  } else {
    // returns object with fields as key and booleans as values
    currentCardLength = cardNo.length.toString();
    // if user inputed cardLength is not equal to the required length
    // or not equal to one of the values in an array of cardLengths
    // regex - matches only numbers
    isCardNumbers = /^\d+$/.test(cardNo);
  }

  if (!isCardNumbers) {
    cardIsInvalid = true;
  } else if (typeof cardLength === 'string') {
    const stringCardLength = currentCardLength.toString();
    cardIsInvalid = stringCardLength !== cardLength;
  } else {
    cardIsInvalid = !cardLength.includes(cardNo.length);
  }

  let pinIsInvalid;
  const currentPinLength = pin.length.toString();

  // only validate if showPin is true
  if (showPin) {
    // regex - check pin is valid by pin type
    const isRegexPassed = pinType !== 'tel' ? /^[a-z0-9]+$/i.test(pin) : /^\d+$/.test(pin);
    if (!isRegexPassed) {
      pinIsInvalid = true;
    } else if (typeof pinLength === 'string') {
      pinIsInvalid = currentPinLength !== pinLength;
    } else {
      pinIsInvalid = !pinLength.includes(pin.length);
    }
  } else pinIsInvalid = false;

  return {
    cardNo: cardIsInvalid,
    pin: pinIsInvalid,
  };
};

export const hasErrors = errors => Object.keys(errors).some(x => errors[x]);

export const isEnabled = (errors, showCaptcha, passedCaptcha, loadingResults) => (!hasErrors(errors) && (showCaptcha === 2 || showCaptcha === 3 || showCaptcha === 0 || passedCaptcha) && !loadingResults);

/**
 * Repeatedly applies a padding character to the user-entered card number, up to a specified length
 * @param {string} cardNumber - The Gift Card Number 
 * @param {Object} paddingConfig - The configuration for how padding is applied to the cardNumber
 * @param {string} paddingConfig.paddingCharacter - The character to be used to pad the cardNumber
 * @param {number} paddingConfig.padUpToLength - The length the cardNumber needs to be padded up to
 * @param {string} prefixConfig - Configuration for how prefix is applied to the cardNumber. Used to determine if entered cardNumber already has a prefix
 * @param {string} prefixConfig.lengthWithPrefix - Once prefix has been applied, length of card number
 * @param {string} prefixConfig.prefixString - Prefix to be applied to card number, used here to determine if prefix has already been entered with input
 * @param {string} prefixConfig.exemptLengths - Input lengths that should NOT have a prefix applied to them 
 * @returns {string} Card Number with padding applied (if configured), otherwise returns originally passed in card number 
 */
export const padCardNumber = (cardNumber, paddingConfig, prefixConfig) => {
  const { paddingCharacter, padUpToLength } = paddingConfig;
  const { lengthWithPrefix, prefixString } = prefixConfig;

  // Boolean to represent if Card Number already has prefix appended
  let cardNumberHasPrefix = cardNumber.length === lengthWithPrefix && cardNumber.substring(0,prefixString.length) === prefixString;

  // Variable to represent cardNumber with the prefix stripped out, if present
  let nonPrefixCardNumber = cardNumberHasPrefix ? cardNumber.slice(6,cardNumber.length) : cardNumber

  if (padUpToLength > nonPrefixCardNumber.length && typeof(padUpToLength) === 'number' && padUpToLength > 0) {
    // Calculate difference in length between the card number and the desired length
    const lengthDifference = padUpToLength - nonPrefixCardNumber.length;

    // Return the padding value of length equal to the difference calculated above
    let paddingString = paddingCharacter.repeat(lengthDifference);

    return paddingString.concat(nonPrefixCardNumber);
    
  } else {
    return cardNumber;
  }
};

/**
 * Applies a predefined, string prefix onto the card number
 * @param {string} cardNumber - The Gift Card Number 
 * @param {string} prefix - String to be prefixed onto cardNumber
 * @returns Prefixed CardNumber (if valid), otherwise returns originally passed in cardNumber
 */
export const prefixCardNumber = (cardNumber, prefix) => {
  if (typeof(prefix) === 'string') {
    return prefix.concat(cardNumber);
  } else {
    return cardNumber
  }
}